import {
  TextureLoader,
  BufferGeometry,
  BufferAttribute,
  Points,
  ShaderMaterial,
  Object3D,
  AdditiveBlending,
  MeshDepthMaterial,
  Vector2,
  Vector3,
  MeshBasicMaterial,
  Color,
  Box3
} from 'three'

import { randomRange } from '@/utils/Maths'
import { Component } from 'shimmer'

import vertexShader from '@/webGL/shaders/particles.vert.js'
import fragmentShader from '@/webGL/shaders/particles.frag.js'
import classicnoise from '@/webGL/shaders/noises/classicnoise2D.js'

export class Particles extends Component {
  constructor ({
    color = 0xffffff,
    opacity = 1,
    count = 200,
    size = new Box3(new Vector3(-50, -50, -50), new Vector3(50, 50, 50) ),
    speed = new Vector2(150, 0),
    particleSize = 15,
    noiseAmp = 1
  } = {}) {
    super({ name: 'Particles' })

    this._size = size
    this.count = count
    this.color = new Color(color)
    this.geometry = new BufferGeometry()
    this.vertices = new Float32Array(count * 3)
    this.progress = new Float32Array(count)

    const sizeVector = new Vector3()
    const centerVector = new Vector3()
    this._size.getSize(sizeVector)
    this._size.getCenter(centerVector)

    this.uniforms = {
      uColor: { type: 'f', value: this.color },
      uTime: { type: 'f', value: 0.0 },
      uSpeed: { type: 'vec2', value: speed },
      uNoiseAmp: { type: 'f', value: noiseAmp },
      uMap: { type: 't', value: null },
      uNoiseTexture: { type: 't', value: null },
      uSize: { type: 'vec3', value: sizeVector },
      uCenter: { type: 'vec3', value: centerVector },
      uParticleSize: { type: 'f', value: particleSize }
    }

    this.setup()
  }

  loadTextures () {
    this.textureParticle = null
    this.textureNoise = null

    return Promise.all([
      new Promise((resolve, reject) => {
        new TextureLoader().load('/textures/particle.png', (texture) => {
          this.textureParticle = texture
          resolve()
        })
      }),
      new Promise((resolve, reject) => {
        new TextureLoader().load('/textures/perlin-noise.jpg', (texture) => {
          this.textureNoise = texture
          resolve()
        })
      })
    ])
  }

  get opacity () {
    return this.uniforms.uOpacity.value
  }

  set opacity (opacity) {
    this.uniforms.uOpacity.value = opacity
  }

  get particleSize () {
    return this.uniforms.uParticleSize.value
  }

  set particleSize (particleSize) {
    this.uniforms.uParticleSize.value = particleSize
  }

  async setup () {
    await this.loadTextures()

    this.uniforms.uMap.value = this.textureParticle
    this.uniforms.uNoiseTexture.value = this.textureNoise

    for (let i = 0; i < this.count; i++) {
      this.vertices[i * 3] = randomRange(this._size.min.x, this._size.max.x)
      this.vertices[i * 3 + 1] = randomRange(this._size.min.y, this._size.max.y)
      this.vertices[i * 3 + 2] = randomRange(this._size.min.z, this._size.max.z)

      this.progress[i] = Math.random() * 100
    }

    this.geometry.setAttribute('position', new BufferAttribute(this.vertices, 3))
    this.geometry.setAttribute('progress', new BufferAttribute(this.progress, 1))

    this.material = new ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true,
      depthTest: false,
      blending: AdditiveBlending,
      vertexShader: classicnoise + vertexShader,
      fragmentShader: fragmentShader
    })

    this.particleSystem = new Points(this.geometry, this.material)
    this.add(this.particleSystem)
  }

  onUpdate ({ elapsed }) {
    this.uniforms.uTime.value = elapsed * 0.01
  }
}
